import { useCallback, useEffect, useReducer } from 'react'
import { useIntl } from 'react-intl'
import { AjvError } from '@rjsf/core'

type FormDataType = Record<string, object>

// TODO fix react hook linting error exhaustive-deps
/* eslint-disable react-hooks/exhaustive-deps */
export function useCustomErrorMessagesMapper<T>(formData: T) {
  const intl = useIntl()
  const [, forceUpdate] = useReducer(x => x + 1, 0)
  const getErrorDomain = (errorPath: string, isParentDomain = false) => {
    const pathArray: string[] = errorPath.split('.')

    return isParentDomain ? pathArray[1] : pathArray[pathArray.length - 1]
  }

  const isFieldDirty = (domain: string, formData?: object): boolean => {
    if (!formData || typeof formData !== 'object') {
      return false
    }

    if (domain in formData) {
      return !!(formData as FormDataType)[domain]
    }

    return Object.values(formData).some(value => {
      return isFieldDirty(domain, value as Record<string, unknown>)
    })
  }

  const filterDirtyFields = (errors: AjvError[], formData?: T) => {
    if (!formData) return errors

    return errors.filter(error => {
      const domain = getErrorDomain(error.property)

      return isFieldDirty(
        domain,
        formData as unknown as Record<string, FormDataType>
      )
    })
  }

  /**
   * Calls a force update function whenever the formData state changes.
   * This is needed because transformErrors callback is called before the latest formData state is updated,
   * so a force update is required to ensure that the latest state is used.
   */
  useEffect(() => {
    forceUpdate()
  }, [formData])

  return useCallback(
    (errors: AjvError[]) => {
      return filterDirtyFields(errors, formData)?.map((error: AjvError) => {
        switch (error.name) {
          case 'required':
            switch (getErrorDomain(error.property)) {
              case 'uniqueUserEmail':
                error.message = intl.formatMessage({
                  id: 'trans__myaccount__errors__empty_email_address',
                })
                break
              case 'dealerAutoComplete':
                error.message = intl.formatMessage({
                  id: 'trans__myaccount__errors__no_results_preferred_dealer',
                })
                break
              default:
                error.message = intl.formatMessage({
                  id: 'trans__myaccount__errors__default',
                })
                break
            }
            break
          case 'pattern':
          case 'regexp':
            switch (getErrorDomain(error.property)) {
              case 'firstName':
                error.message = intl.formatMessage({
                  id: 'trans__myaccount__errors__invalid_firstname_format',
                })
                break
              case 'lastName':
                error.message = intl.formatMessage({
                  id: 'trans__myaccount__errors__invalid_lastname_format',
                })
                break
              case 'phone':
                error.message = intl.formatMessage({
                  id: 'trans__myaccount__errors__invalid_mobile_number_format',
                })
                break
              case 'uniqueUserEmail':
                error.message = intl.formatMessage({
                  id: 'trans__myaccount__errors__invalid_email_address_format',
                })
                break
              case 'identificationNumberRut':
                error.message = intl.formatMessage({
                  id: 'trans__myaccount__errors__identification_number_format',
                })
                break
              case 'password':
                error.message = intl.formatMessage({
                  id: 'trans__myaccount__errors__invalid_password_format',
                })
                break
              default:
                error.message = intl.formatMessage({
                  id: 'trans__myaccount__errors__default',
                })
                break
            }
            break
          case 'maxLength':
            switch (getErrorDomain(error.property)) {
              case 'uniqueUserEmail':
                error.message = intl.formatMessage({
                  id: 'trans__myaccount__errors__invalid_email_address_length',
                })
                break
              case 'firstName':
                error.message = intl.formatMessage({
                  id: 'trans__myaccount__errors__max_firstname_length',
                })
                break
              case 'lastName':
                error.message = intl.formatMessage({
                  id: 'trans__myaccount__errors__max_lastname_length',
                })
                break
              case 'phone':
                error.message = intl.formatMessage({
                  id: 'trans__myaccount__errors__phonenumber_length',
                })
                break
              case 'password':
                error.message = intl.formatMessage({
                  id: 'trans__myaccount__errors__invalid_password_format',
                })
                break
              default:
                error.message = intl.formatMessage({
                  id: 'trans__myaccount__errors__default',
                })
            }
            break
          case 'minLength':
            switch (getErrorDomain(error.property)) {
              case 'firstName':
                error.message = intl.formatMessage({
                  id: 'trans__myaccount__errors__invalid_firstname_format',
                })
                break
              case 'lastName':
                error.message = intl.formatMessage({
                  id: 'trans__myaccount__errors__invalid_lastname_format',
                })
                break
              default:
                error.message = intl.formatMessage({
                  id: 'trans__myaccount__errors__default',
                })
            }
            break
          case 'const':
            switch (getErrorDomain(error.property)) {
              case 'confirmPassword':
                error.message = intl.formatMessage({
                  id: 'trans__myaccount__errors__password_didnt_match',
                })
                break
              default:
                error.message = intl.formatMessage({
                  id: 'trans__myaccount__errors__default',
                })
            }
            break
          default:
            error.message = intl.formatMessage({
              id: 'trans__myaccount__errors__default',
            })
            break
        }

        return error
      })
    },
    [formData]
  )
}
